import {
  FieldGroupOrderingTypes,
  IFieldTypes,
  TrainingType,
} from "../../typings";
import type {
  IFieldFileSelect,
  IFieldReference,
} from "../../typings/object/field";
import type { TrainingConfig } from "../../typings/training";
import type { TWorkflow } from "../../typings/workflow";
import {
  trainingEndDateString,
  trainingStartDateString,
} from "../custom-fields";

const defaultTrainingTemplateRoot = "system-training-template";
export const trainingAttachCertificateString = "training-attach-certificate";

export const defaultTrainingTemplateWithExpiryDateId = `${defaultTrainingTemplateRoot}-00001-with-expiry-date`;
export const defaultTrainingTemplateNoExpiryDateId = `${defaultTrainingTemplateRoot}-00002-no-expiry-date`;

export const defaultTrainingTemplateWithExpiryDateName =
  "Default Training Template (With Expiry Date)";
export const defaultTrainingTemplateNoExpiryDateName =
  "Default Training Template (No Expiry Date)";
export const defaultTrainingSectionLabel = "Training Details";

export const DefaultTrainingNoApprovalWorkflow: TWorkflow = {
  _meta: {
    start: "default",
    end: "_done",
    _version: 2,
    nonLinearWorkflow: false,
    transitions: {
      "_create:default": {
        key: "_create:default",
        source: "_create",
        target: "default",
      },
      "default:_done": {
        key: "default:_done",
        source: "default",
        target: "_done",
      },
      "_done:default": {
        key: "_done:default",
        source: "_done",
        target: "default",
        skipValidation: true,
      },
    },
  },
  default: {
    name: "Default",
  },
  _done: {
    name: "Complete",
    _meta: {
      isResolution: true,
    },
  },
};

export function generateDefaultTrainingSection(
  hasEndDate?: boolean,
): Pick<TrainingConfig, "ordering" | "sections"> {
  const dateObtainedRef: IFieldReference = {
    fieldId: trainingStartDateString,
    name: trainingStartDateString,
    description: "The date you completed the training or certification",
    label: "Date Obtained",
    required: true,
  };

  const attachCertificateField: IFieldFileSelect = {
    fieldType: IFieldTypes.fileSelect,
    name: trainingAttachCertificateString,
    description: "Copy of Certificate",
    label: "Attach Certificate",
    required: true,
    readonly: false,
    span: 4,
    multiple: false,
  };

  const fields: (IFieldFileSelect | IFieldReference)[] = [dateObtainedRef];
  const ordering = [
    {
      type: FieldGroupOrderingTypes.field,
      orderIdx: 0,
      entryIdx: 0,
      name: trainingStartDateString,
    },
  ];

  if (hasEndDate) {
    const expiryDateRef: IFieldReference = {
      fieldId: trainingEndDateString,
      name: trainingEndDateString,
      description: "The date this training or certification will expire",
      label: "Expiry Date",
      required: true,
    };

    fields.push(expiryDateRef);
    ordering.push({
      orderIdx: ordering.length,
      entryIdx: fields.length - 1,
      type: FieldGroupOrderingTypes.field,
      name: trainingEndDateString,
    });
  }

  fields.push(attachCertificateField);
  ordering.push({
    orderIdx: ordering.length,
    entryIdx: fields.length - 1,
    type: FieldGroupOrderingTypes.field,
    name: trainingAttachCertificateString,
  });

  return {
    ordering: ["default"],
    sections: {
      default: {
        name: "default",
        label: defaultTrainingSectionLabel,
        conditions: [
          {
            value: ["_done"],
            parameter: "[[state]]",
            operation: "in",
            action: "readonly",
            _source: "workflow",
          },
        ],
        permissionGuard: [
          {
            stateAssignee: ["default"],
            action: "visible",
            _source: "workflow",
          },
        ],
        groups: [],
        fields,
        ordering,
      },
    },
  };
}

export type DefaultTrainingTemplateDefinition = {
  id: string;
  training: TrainingConfig;
};

function createDefaultTrainingTemplateDefinition({
  id,
  name,
  hasEndDate,
}: {
  id: string;
  name: string;
  hasEndDate: boolean;
}): DefaultTrainingTemplateDefinition {
  return {
    id,
    training: {
      name,
      type: TrainingType.training,
      workflow: structuredClone(DefaultTrainingNoApprovalWorkflow),
      hasEndDate,
      ...generateDefaultTrainingSection(hasEndDate),
    },
  };
}

export const defaultTrainingTemplateDefinitions: DefaultTrainingTemplateDefinition[] =
  [
    createDefaultTrainingTemplateDefinition({
      id: defaultTrainingTemplateWithExpiryDateId,
      name: defaultTrainingTemplateWithExpiryDateName,
      hasEndDate: true,
    }),
    createDefaultTrainingTemplateDefinition({
      id: defaultTrainingTemplateNoExpiryDateId,
      name: defaultTrainingTemplateNoExpiryDateName,
      hasEndDate: false,
    }),
  ];
